import Slider from '@ant-design/react-slick';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useState, useEffect, useRef } from 'react';
import Lightbox from 'react-awesome-lightbox';
import 'react-awesome-lightbox/build/style.css';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { combineLatest } from 'rxjs';

import META_DATA from '../../constants/metaData';
import CartStore, { productsForOrder, productsQuantities } from '../../store/cart';
import ConfigsMYOStore from '../../store/configsMYO';
import Cookie from '../../store/cookies';
import DeliveryMode from '../../store/deliveryMode';
import OrderLimitationsStore from '../../store/orderLimitations';
import ProductsCollection from '../../store/productsCollection';
import { earlyInUseEffect } from '../../utils';
import { addToAnalytics, getProductInfo } from '../../utils/analytics';
import { productImageURL, getProductOrderLimit, productsLimitReached } from '../../utils/products';
import CustomMetaHead from '../CustomMetaHead';
import FadeInOutError from '../FadeInOutError/FadeInOutError';
import ListButton from '../ListButton';
import ButtonCart from '../RoundButton/ButtonCart';
import NextArrow from '../swiper-buttons/NextArrow';
import PrevArrow from '../swiper-buttons/PrevArrow';

import DetailsExtraInfo from './DetailsExtraInfo';
import NutritionDetails from './NutritionDetails';
import ProductChoices from './ProductChoices';
import ProductComment from './ProductComment';
import styles from './ProductDetails.module.scss';

const SLIDER_SETTINGS = {
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  prevArrow: <PrevArrow smallArrow />,
  nextArrow: <NextArrow smallArrow />,
  className: styles.slider,
};

const SMALL_SLIDER_SETTINGS = {
  infinite: false,
  speed: 500,
  slidesToShow: 4,
  slidesToScroll: 1,
  prevArrow: <PrevArrow smallArrow />,
  nextArrow: <NextArrow smallArrow />,
  className: styles.smallSlider,
};

export default function ProductDetails({
  productDetails,
  _product,
  isProductAvailable,
  badgeText,
  imgSrc,
  title,
  description,
  productLowestPrice,
}) {
  const sliderRef = useRef();
  const previewSliderRef = useRef();
  const [arrayImg, setArrayImg] = useState([]);
  const [currentImg, setCurrentImg] = useState('');
  const [productQuantity, setProductQuantity] = useState({});
  const [productLimit, setProductLimit] = useState({});
  const [product, setProduct] = useState();
  const [config, setConfig] = useState('');
  const [isProductMYO, setIsProductMYO] = useState('');
  const [configDefaultOption, setConfigDefaultOption] = useState('');
  const [isCartVisible, setIsCartVisible] = useState(false);
  const [hasReachedMaxQuantity, setHasReachedMaxQuantity] = useState(false);

  function returnUomTag(uom) {
    if (uom === 'ΚΙΛ') {
      return 'κιλό';
    }
    if (uom === 'ΛΙΤ') {
      return 'λίτρο';
    }
    if (uom === 'ΜΕΖ') {
      return 'μεζούρα';
    }
    if (uom === 'TEM') {
      return 'τεμάχιο';
    }
    if (uom === 'ΜΕΤΡΟ') {
      return 'μέτρο';
    }
    if (uom === 'ΤΜ') {
      return 'τ.μ.';
    }
  }

  function onClickAlternative(index, img) {
    sliderRef.current.slickGoTo(index);
    previewSliderRef.current.slickGoTo(index);
    setCurrentImg(img);
  }

  function getProductFromCart() {
    const productsInCart = productsForOrder.getValue();
    const productInCart = productsInCart.find(p => p.sku === _product.sku);
    return productInCart || [];
  }

  function getCollectionType(collectionTypes, collection) {
    if (isEmpty(collection)) {
      return;
    }

    if (collectionTypes?.length > 1) {
      if (!collection[collectionTypes[0]] || !collection[collectionTypes[1]]) {
        return;
      }
      const easyProduct = collection[collectionTypes[0]].find(p => p.sku === _product.sku);
      const localProduct = collection[collectionTypes[1]].find(p => p.sku === _product.sku);
      if (easyProduct) {
        return collectionTypes[0];
      }
      if (localProduct) {
        return collectionTypes[1];
      }
      console.debug('no collection for this product');
    }
    return collectionTypes[0];
  }

  useEffect(() => {
    const isCartVisible$ = CartStore.getIsVisble$().subscribe(setIsCartVisible);
    const modeAndCollection$ = combineLatest([DeliveryMode.lazyInit(), ProductsCollection.subject]).subscribe(res => {
      const collectionTypes = DeliveryMode.getCollectionAsNumber(res[0]);
      const collectionType = getCollectionType(collectionTypes, res[1]);
      const productsOfCollection = res[1][collectionType];

      if (!isEmpty(productsOfCollection)) {
        const updatedProduct = productsOfCollection.find(p => p.sku === _product.sku);
        if (updatedProduct) {
          const productInCart = getProductFromCart();
          const finalProduct = {
            ...productInCart,
            ...updatedProduct,
          };
          setProduct(finalProduct);
        }
      }
    });

    const privacy = Cookie.getValue();
    if (privacy && privacy.performance) {
      addToAnalytics('ecomProductShow', getProductInfo(productDetails));
    }

    return () => {
      isCartVisible$.unsubscribe();
      modeAndCollection$.unsubscribe();
    };
  }, [_product]);

  useEffect(() => {
    if (!product) return earlyInUseEffect;

    const productQuantityAndLimits$ = combineLatest([
      productsQuantities.lazyInit(),
      OrderLimitationsStore.subject,
    ]).subscribe(res => {
      const _productQuantity = res[0][product._id];
      setProductQuantity(_productQuantity);
      const limit = getProductOrderLimit(res[1], product);
      setProductLimit(limit);
      productsLimitReached(product, _productQuantity, setHasReachedMaxQuantity, limit);
    });

    return () => {
      productQuantityAndLimits$.unsubscribe();
    };
  }, [product]);

  useEffect(() => {
    if (!product) return earlyInUseEffect;
    const { primary, alternatives = [] } = product.images;
    const imageObjects = [primary, ...alternatives];
    const imageUrls = imageObjects.map(img => productImageURL(img, 'productDetailsTile'));
    setArrayImg(imageUrls);
    const configsMyo$ = ConfigsMYOStore.subscribe(configs => {
      const configApplied = configs.find(c => c.productSkus.includes(product.sku));
      if (!configApplied) {
        setIsProductMYO(false);
        return;
      }
      const defaultOption = configApplied.options.find(option => option._id === configApplied.defaultOptionsId);
      const isThisProductMYO = configApplied?.productSkus.includes(product.sku);
      setIsProductMYO(isThisProductMYO);
      setConfigDefaultOption(defaultOption);
      setConfig(configApplied);
    });

    return () => {
      configsMyo$.unsubscribe();
    };
  }, [product]);

  if (!_product) return null;
  const meta = {
    ...META_DATA[process.env.appName].productDetails,
    url: `${process.env.domain}/${_product.slug}/`,
    description: `${title} - ${description}. ${productDetails.description || ''}`,
    title: _product.name,
    image: imgSrc,
    keywords: _product.tags.join(','),
  };
  return (
    <>
      <CustomMetaHead meta={meta} />
      {currentImg && (
        <Lightbox
          image={currentImg}
          title={title}
          allowRotate={false}
          allowReset={false}
          onClose={() => setCurrentImg('')}
        />
      )}
      <section
        className={cn(styles.productDetailsContainer, {
          [styles.spaceOpenCart]: isCartVisible,
        })}
      >
        <div className={styles.productDetailsSliders}>
          <div className={styles.sliders}>
            <div className={styles.productImgContainerWrapper}>
              <div className={styles.productImgContainer}>
                <Slider {...SLIDER_SETTINGS} ref={sliderRef}>
                  {arrayImg.map(img => (
                    <div key={img}>
                      <div className={styles.container}>
                        <button className={styles.openOverLayImg} onClick={() => setCurrentImg(img)}>
                          <img className={styles.imgSlider} width="286" height="286" src={img} alt={title} />
                        </button>
                      </div>
                    </div>
                  ))}
                </Slider>
              </div>
            </div>

            <Slider {...SMALL_SLIDER_SETTINGS} ref={previewSliderRef}>
              {arrayImg.map((img, i) => (
                <button
                  className={styles.imgButtons}
                  key={img}
                  type="button"
                  onClick={() => onClickAlternative(i, img)}
                >
                  <img className={styles.smallSliderImg} width="48" height="48" src={img} alt={title} />
                </button>
              ))}
            </Slider>
          </div>
          <div className={styles.productDetailsInfo}>
            <section className={styles.upperSection}>
              <div>
                <div className={styles.headerContainer}>
                  <div>{badgeText && <p className={styles.offerPercentage}>{badgeText}</p>}</div>
                  {product && <ListButton product={product} isProductDetails />}
                  {product && (
                    <div className={styles.buttonCartSmallScreen}>
                      <ButtonCart
                        product={product}
                        productLimit={productLimit}
                        quantity={productQuantity}
                        isProductMYO={isProductMYO}
                        configDefaultOption={configDefaultOption}
                        removed={!isProductAvailable}
                        squared
                        isEditable
                      />
                      {hasReachedMaxQuantity && <FadeInOutError />}
                    </div>
                  )}
                </div>
                <h1 className={styles.productName}>{title}</h1>
                <p className={styles.productCode}>
                  <span className={styles.boldText}>
                    <IoCheckmarkCircle className={styles.checkMarkImg} alt="checkmark" />
                    {description}
                  </span>
                  <span>{`- Κωδ. προϊόντος ${_product.sku}`}</span>
                </p>
                <div className={styles.middleContainer}>
                  <div className={styles.textFlex}>
                    {product &&
                      (product.uom === 'ΚΙΛ' ? (
                        <p className={styles.priceText}>
                          <span className={styles.price}>
                            {`€ ${((product.finalPrice * product.stepToUom) / 100).toFixed(2)} `}
                          </span>
                          {product.offerType !== 'none' && product.beginPrice > product.finalPrice && (
                            <span className={cn(styles.lineThroughText, styles.greyPrice)}>
                              {`${((product.beginPrice * product.stepToUom) / 100).toFixed(2)} `}
                            </span>
                          )}
                          {`ανά ${1000 * product.stepToUom}γρ.`}
                        </p>
                      ) : (
                        <p className={styles.priceText}>
                          <span className={styles.price}>{`€ ${(product.finalPrice / 100).toFixed(2)} `}</span>
                          {product.offerType !== 'none' && product.beginPrice > product.finalPrice && (
                            <span className={cn(styles.lineThroughText, styles.greyPrice)}>
                              {`${((product.beginPrice * product.stepToUom) / 100).toFixed(2)} `}
                            </span>
                          )}
                          {`ανά ${returnUomTag(product.uom)}`}
                        </p>
                      ))}
                    {product &&
                      (product.uom === 'ΚΙΛ' ? (
                        <p className={cn(styles.greyPriceText)}>
                          <span className={cn(styles.greyPrice)}>{`€ ${(
                            product.unitOfMeasurementFinalPrice / 100
                          ).toFixed(2)} `}</span>

                          {product.offerType !== 'none' && product.beginPrice > product.finalPrice && (
                            <span className={cn(styles.lineThroughText, styles.grey)}>
                              {`${(product.unitOfMeasurementBeginPrice / 100).toFixed(2)} `}
                            </span>
                          )}
                          {`ανά ${product.quantity}`}
                        </p>
                      ) : product?.unitOfMeasurement && returnUomTag(product.unitOfMeasurement) ? (
                        <p className={cn(styles.greyPriceText)}>
                          <span className={styles.greyPrice}>{`€ ${(product.unitOfMeasurementFinalPrice / 100).toFixed(
                            2
                          )} `}</span>

                          {product.offerType !== 'none' && product.beginPrice > product.finalPrice && (
                            <span className={cn(styles.grey, styles.lineThroughText)}>
                              {`${(product.unitOfMeasurementBeginPrice / 100).toFixed(2)} `}
                            </span>
                          )}
                          {`ανά ${returnUomTag(product.unitOfMeasurement)}`}
                        </p>
                      ) : null)}
                    {product && product.offerType !== 'none' && productLowestPrice && productLowestPrice.price && (
                      <div className={styles.lowestPriceContainer}>
                        <div className={styles.stocksIcon} />
                        <span className={cn(styles.lowestPriceText)}>{`Χαμηλότερη τιμή  30 ημερών: ${
                          product.uom === 'ΚΙΛ'
                            ? (productLowestPrice.price / 100).toFixed(2)
                            : (productLowestPrice.price / 100).toFixed(2)
                        } ${product.uom === 'ΚΙΛ' ? '€ /' : '€'}
                         ${product.uom === 'ΚΙΛ' ? returnUomTag(product.unitOfMeasurement) : ''}`}</span>
                      </div>
                    )}
                  </div>

                  {product && (
                    <div className={styles.buttonCart}>
                      <ButtonCart
                        product={product}
                        productLimit={productLimit}
                        quantity={productQuantity}
                        isProductMYO={isProductMYO}
                        configDefaultOption={configDefaultOption}
                        squared
                        removed={!isProductAvailable}
                        isEditable
                      />
                      {hasReachedMaxQuantity && <FadeInOutError />}
                    </div>
                  )}
                </div>
              </div>

              {config && (
                <ProductChoices product={product} setProduct={setProduct} config={config} isProductMYO={isProductMYO} />
              )}
              {product && <ProductComment product={product} setProduct={setProduct} />}
            </section>
            {productDetails.description && (
              <div className={styles.productDesciption}>
                <p className={styles.desciptionTitle}>Περιγραφή</p>
                <p className={styles.desciptionText}>{productDetails.description}</p>
              </div>
            )}
            <DetailsExtraInfo productDetails={productDetails} />
            <NutritionDetails productDetails={productDetails} />
          </div>
        </div>
      </section>
    </>
  );
}
